import { Injectable, OnDestroy } from '@angular/core';
import { Client, IMessage, ReconnectionTimeMode, StompSubscription } from '@stomp/stompjs';
import * as SockJS from 'sockjs-client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { EnvService } from '../helpers/env.service';

/**
 * Low-level STOMP over SockJS. Connect with {@link connectWithJwt};
 * use {@link watch} per destination; subscriptions are re-established after reconnect.
 *
 * SockJS URL: {@link EnvService.WS_BASE_URL} + {@link EnvService.WS_NOTIFICATION_PATH}
 * STOMP ping/pong: {@link Client} uses `heartbeatOutgoing` / `heartbeatIncoming` (negotiated `heart-beat` with broker).
 */
@Injectable({
    providedIn: 'root',
})
export class StompService implements OnDestroy {
    /** Last SockJS URL passed to `webSocketFactory` (for debug only). */
    private lastSockJsUrl = '';

    private client: Client | null = null;
    private readonly connected$ = new BehaviorSubject<boolean>(false);
    private readonly destinationSubjects = new Map<string, Subject<IMessage>>();
    private stompSubscriptions = new Map<string, StompSubscription>();

    /**
     * Timestamp (ms) of last STOMP frame received on any watched destination.
     * STOMP heartbeats are not surfaced as messages; this updates on real MESSAGE frames only.
     */
    readonly lastStompMessageAt$ = new BehaviorSubject<number | null>(null);

    constructor(private env: EnvService) {}

    ngOnDestroy(): void {
        this.disconnect();
    }

    getConnectionState(): Observable<boolean> {
        return this.connected$.asObservable();
    }

    get isConnected(): boolean {
        return this.client?.connected ?? false;
    }

    /**
     * Connects with JWT sent as STOMP header **`jwtToken`** (caller reads `localStorage` key **`jwt`**).
     *
     * ### Heartbeat (`WS_STOMP_HEARTBEAT_MS`)
     * Sets both `heartbeatOutgoing` (client → broker ping) and `heartbeatIncoming` (expected broker → client pong),
     * negotiated via the STOMP `heart-beat` header on CONNECT.
     *
     * **Why heartbeats matter for push notifications:**
     * NAT gateways, reverse proxies, and cloud load balancers silently drop idle TCP connections — typically
     * after 30 s–5 min of inactivity. Without heartbeats the STOMP client remains unaware (the socket appears
     * open) and never triggers a reconnect. Any notification the server sends during that window is lost.
     * Heartbeat frames keep the connection alive through these middleboxes and, when a pong is missed, cause
     * the client to close and re-establish the connection via `reconnectDelay`.
     *
     * **Tuning guidance:**
     * - `10 000` ms (default) — safe across most infrastructure; higher server-side load.
     * - `30 000`–`60 000` ms — reasonable for notification-only traffic; reduce if your proxy/LB timeout is short.
     * - `0` — disables heartbeats entirely. Only safe in environments where the full network path is guaranteed
     *   never to drop idle connections (e.g. same-host loopback). Avoid in production.
     */
    connectWithJwt(jwt: string): void {
        if (this.env.WS_DISABLED) {
            return;
        }

        if (!jwt?.trim()) {
            this.disconnect();
            return;
        }

        const base = (this.env.WS_BASE_URL || '').replace(/\/$/, '');
        const path = this.env.WS_NOTIFICATION_PATH || '/ws-notification';
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;
        const url = `${base}${normalizedPath}`;

        this.disconnect();
        this.lastSockJsUrl = url;

        const rcMs =
            typeof this.env.WS_RECONNECT_DELAY_MS === 'number' &&
                this.env.WS_RECONNECT_DELAY_MS >= 0
                ? this.env.WS_RECONNECT_DELAY_MS
                : 10000;
        const hbMs =
            typeof this.env.WS_STOMP_HEARTBEAT_MS === 'number' &&
                this.env.WS_STOMP_HEARTBEAT_MS >= 0
                ? this.env.WS_STOMP_HEARTBEAT_MS
                : 10000;

        this.client = new Client({
            webSocketFactory: () => {
                const Ctor = (SockJS as unknown as { default?: new (u: string) => object })
                    .default ?? (SockJS as unknown as new (u: string) => object);
                return new Ctor(url) as WebSocket;
            },
            connectHeaders: { token: jwt.trim() },
            reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
            reconnectDelay: rcMs,
            maxReconnectDelay: rcMs * 6,
            /** STOMP ping/pong (LF frames per spec); negotiated via CONNECT `heart-beat` header. */
            heartbeatIncoming: hbMs,
            heartbeatOutgoing: hbMs,
            /**
             * If broker stops answering heartbeats, close socket sooner (helps reconnect).
             */
            discardWebsocketOnCommFailure: true,
            debug: (message: unknown) => {
                if (!localStorage.getItem('enableDebug')) {
                    return;
                }
                // @stomp/stompjs logs `connected to server ${frame.headers.server}`; `server` is optional in CONNECTED.
                if (
                    typeof message === 'string' &&
                    message === 'connected to server undefined'
                ) {
                    console.log(
                        '[Stomp]',
                        'connected (broker omitted optional CONNECTED `server` header)',
                        this.lastSockJsUrl ? `— ${this.lastSockJsUrl}` : '',
                    );
                    return;
                }
                console.log('[Stomp]', message);
            },
            onConnect: () => {
                this.clearStompSubscriptions();
                this.connected$.next(true);
                for (const dest of this.destinationSubjects.keys()) {
                    this.attachDestination(dest);
                }
            },
            onDisconnect: () => {
                this.connected$.next(false);
            },
            onStompError: (frame) => {
                console.error('[Stomp]', frame.headers['message'], frame.body);
            },
        });

        this.client.activate();
    }

    disconnect(): void {
        this.clearStompSubscriptions();
        if (this.client) {
            this.client.deactivate();
            this.client = null;
        }
        this.connected$.next(false);
        this.lastStompMessageAt$.next(null);
    }

    /**
     * Multicast stream for a STOMP destination. Register before or after connect.
     */
    watch(destination: string): Observable<IMessage> {
        let subj = this.destinationSubjects.get(destination);
        if (!subj) {
            subj = new Subject<IMessage>();
            this.destinationSubjects.set(destination, subj);
            if (this.client?.connected) {
                this.attachDestination(destination);
            }
        }
        return subj.asObservable().pipe(
            tap(() => this.lastStompMessageAt$.next(Date.now())),
        );
    }

    private attachDestination(destination: string): void {
        if (!this.client?.connected || this.stompSubscriptions.has(destination)) {
            return;
        }
        const subj = this.destinationSubjects.get(destination);
        if (!subj) {
            return;
        }
        const stompSub = this.client.subscribe(destination, (msg) =>
            subj.next(msg),
        );
        this.stompSubscriptions.set(destination, stompSub);
    }

    private clearStompSubscriptions(): void {
        for (const s of this.stompSubscriptions.values()) {
            try {
                s.unsubscribe();
            } catch {
                /* ignore */
            }
        }
        this.stompSubscriptions.clear();
    }
}
